import React, { useEffect, useState } from 'react'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { ContentCard, Flex, InfoIcon, TextVariant, Button, useModal, Text, ButtonVariant } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { StyledBiennale } from 'components'
import { useRouter } from 'hooks/useRouter'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import { useRewardProxyContractAddress, useStakeContractAddress } from 'state/global/hooks'
import { BNToNumberWithDecimals } from '@mprolab/sdk'
import { useMproRewardProxyContract } from 'hooks/contracts/useMproRewardProxyContract'
import { BigNumber } from 'ethers'
import { logMessage } from 'utils/logMessage'
import { CheckCircle, Info } from 'react-feather'

import ClaimHistoryModal from './ClaimHistoryModal'
import AutostakeInfo from './AutostakeInfo'
import JoinAutostake from '../widgets/JoinAutostake'
import { useDeclarationCounter, useGetAvailableStake, useGetStakeReward } from '../autoStake.logic'
import ClaimRewardModal from '../widgets/ClaimRewardModal'

const HeaderWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`

const RewardWrapper = styled(Flex)`
  padding: 24px;
  background: ${({ theme }) => theme.colors.gray10};
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  min-height: 64px;
  margin-bottom: 16px;
  ${({ theme }) => theme.mediaQueries.tablet} {
    padding: 24px 10px;
    height: 79px;
  }
`

const StyledText = styled(Text)`
  white-space: 'pre-wrap';
`

const RewardToClaim: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { push } = useRouter()
  const isMobile = useThemeMediaQuery('mobile')
  const { chainId: currentChainId, account } = useWeb3React()
  const { setupNetwork } = useSetupNetwork()
  const {
    totalReward,
    totalRewardLoading,
    withheldMpro,
    withheldLicenses,
    joinedAutostake,
    claimable,
    hasOnlyMGALicenses,
    activeStake,
    hasMGALicense
  } = useGetStakeReward()

  const { availableStakeAddress: availableStake, isLoading: isLoadingAvailableStake } = useGetAvailableStake()
  const availableStakeAddress = availableStake || activeStake
  const { getReward } = useMproRewardProxyContract()
  const { timeUntilEnd, progress, counterLoading, disableCounter } = useDeclarationCounter(
    joinedAutostake,
    availableStakeAddress,
    isLoadingAvailableStake
  )

  const stakeContractAddresses = useStakeContractAddress()
  const mproRewardProxyContracts = useRewardProxyContractAddress()
  const mproStakeRewardContract = stakeContractAddresses?.find((contract) => contract.address === availableStakeAddress)
  const mproRewardProxyActiveContract = mproRewardProxyContracts?.find((contract) => contract.isActive) ?? mproRewardProxyContracts?.[0]
  const rewardProxyChainId = mproRewardProxyActiveContract?.chainId
  const [openClaimHistoryModal] = useModal(<ClaimHistoryModal rewardProxyChainId={rewardProxyChainId} />, false)
  const invalidNetwork = currentChainId !== rewardProxyChainId
  const stakeActionPeriodEnded = timeUntilEnd <= 0

  const [rewardToClaim, setRewardToClaim] = useState(BigNumber.from(0))
  const [rewardToClaimLoading, setRewardToClaimLoading] = useState(true)

  const [openAutostakeModal] = useModal(
    <JoinAutostake {...{ timeUntilEnd, progress, counterLoading, availableStakeAddress }} />
  )
  const [openClaimModal] = useModal(
    <ClaimRewardModal mproRewardProxyActiveContractChainId={rewardProxyChainId} />,
    false,
    undefined,
    () => {
      setRewardToClaimLoading(true)
      if (!account || !rewardProxyChainId) {
        setRewardToClaimLoading(false)
        return
      }
      getReward(account, rewardProxyChainId)
        .then((reward) => {
          setRewardToClaim(reward._rewardToClaim)
        })
        .catch((error) => {
          // An unreachable RPC must surface as a stale amount, never as a spinner
          // that never stops — that is what users saw during the 2026-08 outage.
          logMessage('Error while refreshing reward to claim', error)
        })
        .finally(() => {
          setRewardToClaimLoading(false)
        })
    }
  )

  useEffect(() => {
    ;(async () => {
      try {
        if (account && rewardProxyChainId) {
          const reward = await getReward(account, rewardProxyChainId)
          setRewardToClaim(reward._rewardToClaim)
        }
      } catch (error: any) {
        logMessage('Error while fetching reward to claim', error)
      } finally {
        setRewardToClaimLoading(false)
      }
    })()
  }, [account, rewardProxyChainId, getReward])

  const displayMPROReward = () => {
    switch (true) {
      case totalRewardLoading:
        return <UseAnimations animation={loading} strokeColor={colors.gray6} size={32} />
      case joinedAutostake && !hasMGALicense:
        return (
          <StyledText textAlign="center" variant={TextVariant.BODY_DEFAULT_BOLD}>
            {t('autoStake.joinedAutostakeInfo')}
          </StyledText>
        )
      default:
        return (
          <Flex flexDirection="column" alignItems="center" gap="4px">
            {/* Everything the licences have earned — including what is
                currently reserved for a buyer. Showing only the spendable
                figure made a listing look like a loss. */}
            <StyledBiennale variant={TextVariant.H5}>
              {`${(totalReward + withheldMpro).toFixed(2)} MPRO`}
            </StyledBiennale>
            {withheldMpro > 0 && (
              <Flex alignItems="center" gap="6px">
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                  {t('rewards.availableAmount', { amount: totalReward.toFixed(2) })}
                </Text>
                <InfoIcon
                  iconProps={{ size: 14 }}
                  tooltipProps={{ title: t('rewards.availableAmountInfo') }}
                />
              </Flex>
            )}
          </Flex>
        )
    }
  }

  const displayMPRORewardToClaim = () => {
    switch (true) {
      case rewardToClaimLoading:
        return <UseAnimations animation={loading} strokeColor={colors.gray6} size={32} />
      default:
        return (
          <>
            <StyledBiennale variant={TextVariant.H5}>{`${BNToNumberWithDecimals(rewardToClaim).toFixed(
              2
            )} MPRO`}</StyledBiennale>
          </>
        )
    }
  }

  const joinStakeButton = (): {
    handler: () => void
    description: string
  } => {
    if (currentChainId !== mproStakeRewardContract?.chainId) {
      return {
        handler: async () => {
          await setupNetwork({ chainId: mproStakeRewardContract?.chainId, modal: true, error: false })
        },
        description: t('general.switchNetwork')
      }
    }
    if (joinedAutostake) {
      return {
        handler: () => {
          push('/stake')
        },
        description: t('rewards.goToStakingPanel')
      }
    }
    return {
      handler: openAutostakeModal,
      description: t('rewards.joinAutostake')
    }
  }

  const claimRewardButton = (): {
    handler: () => void
    description: string
  } => {
    if (!rewardProxyChainId) {
      return {
        handler: () => undefined,
        description: t('rewards.claimReward')
      }
    }

    if (currentChainId !== rewardProxyChainId) {
      return {
        handler: async () => {
          const isSwitched = await setupNetwork({
            chainId: rewardProxyChainId,
            modal: true,
            error: false
          })
          if (isSwitched) openClaimModal()
        },
        description: t('rewards.claimReward')
      }
    }
    return {
      handler: openClaimModal,
      description: t('rewards.claimReward')
    }
  }

  const getWarning = () => {
    switch (true) {
      case hasOnlyMGALicenses && timeUntilEnd > 0 && !disableCounter:
        return (
          <Flex
            p="8px"
            fullWidth
            gap="16px"
            height="131px"
            border={`1px solid ${colors.yellow}`}
            borderRadius="8px"
            background={colors.gray10}
            alignItems="center"
            justifyContent="center"
          >
            <Info size={22} color={colors.yellow} />
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.yellow}>
              The autostake function is not available for MGA licenses.
            </Text>
          </Flex>
        )
      case hasMGALicense && timeUntilEnd > 0 && !disableCounter:
        return (
          <Flex
            p="8px"
            fullWidth
            gap="16px"
            border={`1px solid ${colors.yellow}`}
            borderRadius="8px"
            background={colors.gray10}
            alignItems="center"
          >
            <Info size={22} color={colors.yellow} />
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.yellow}>
              MGA licenses do not participate in the Autostake
            </Text>
          </Flex>
        )
      default:
        return <></>
    }
  }

  const rewardButton = (
    <Flex flexDirection="column" gap="16px">
      {mproStakeRewardContract && (
        <>
          {joinedAutostake ? (
            <Flex
              p="8px"
              fullWidth
              gap="16px"
              height="77px"
              borderRadius="8px"
              background={colors.gray10}
              alignItems="center"
              justifyContent="center"
            >
              <CheckCircle size={22} color={colors.green} />
              <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.green}>
                Joined Autostake
              </Text>
            </Flex>
          ) : (
            <AutostakeInfo
              {...{ joinedAutostake }}
              button={
                <Button
                  fullWidth
                  size="sm"
                  onClick={() => joinStakeButton().handler()}
                  disabled={stakeActionPeriodEnded}
                >
                  {joinStakeButton().description}
                </Button>
              }
            />
          )}
        </>
      )}
      {getWarning()}

      {(hasMGALicense || (joinedAutostake && rewardToClaim.gt(0)) || !joinedAutostake) && (
        <Flex alignItems="center" justifyContent="flex-end">
          <Button
            variant={ButtonVariant.OUTLINED}
            disabled={!rewardProxyChainId || (rewardToClaim.eq(0) && totalReward === 0)}
            onClick={() => claimRewardButton().handler()}
            style={{ padding: '10px 24px', height: '40px' }}
          >
            {claimRewardButton().description}
          </Button>
        </Flex>
      )}
    </Flex>
  )

  return (
    <ContentCard fullWidth maxHeight="100%" flexDirection="column" p="24px">
      <HeaderWrapper>
        <Flex gap="8px" alignItems="center">
          <StyledBiennale variant={TextVariant.BODY_BIG_BOLD}>My distribution</StyledBiennale>
          <InfoIcon
            iconProps={{ size: 18 }}
            tooltipProps={{
              title: t('rewards.rewardToClaimInfo'),
              placement: 'top'
            }}
          />
        </Flex>
        {account && (
          <Button
            onClick={openClaimHistoryModal}
            variant={ButtonVariant.TEXT}
            p="0px"
            style={{ textDecoration: 'none', height: 'auto' }}
          >
            <Text clickable variant={TextVariant.BODY_SMALL} color={colors.purple}>
              Claim history
            </Text>
          </Button>
        )}
      </HeaderWrapper>
      {totalRewardLoading || counterLoading ? (
        <Flex justifyContent="center" alignItems="center">
          <UseAnimations animation={loading} strokeColor={colors.gray6} size={64} />
        </Flex>
      ) : (
        <>
          <Flex fullWidth gap="16px" pb="16px" flexDirection={isMobile ? 'column' : 'row'}>
            <Flex fullWidth flexDirection="column" gap="6px">
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>To mint</Text>
              <RewardWrapper>{displayMPROReward()}</RewardWrapper>
            </Flex>
            <Flex fullWidth flexDirection="column">
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>Ready to claim</Text>
              <RewardWrapper>{displayMPRORewardToClaim()}</RewardWrapper>
            </Flex>
          </Flex>
          {rewardButton}
        </>
      )}
    </ContentCard>
  )
}

export default RewardToClaim
